import React, { useEffect } from 'react'
import { Button, Dialog, Typogaphy } from 'vnetwork-ui'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { useForm } from 'react-hook-form'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { FormItem } from 'vnetwork-ui'
import { useTranslation } from 'react-i18next'
import { TFunction } from 'i18next'
import { UpdateWorkNDay } from '@/modules/WorkDay/services'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'

interface UpdateWorkNDayProps {
    open: boolean
    selectedWorkDayId: number | null
    workNDay: number
    workDayNote: string
    onClose: () => void
    refetchData: () => void
}
const updateWorkDaySchema = (t: TFunction<'translation', undefined>) =>
    yup.object({
        work_day_note: yup.string().trim().required(t('blank-errors')),
        work_n_day: yup.number().required(t('blank-errors')),
    })

const UpdateWorkDay: React.FC<UpdateWorkNDayProps> = ({
    open,
    selectedWorkDayId,
    workDayNote,
    workNDay,
    onClose,
    refetchData,
}) => {
    const { t } = useTranslation()

    const { mutateAsync: updateWorkNDay } = useMutationWithHandler(
        {
            mutationFn: UpdateWorkNDay,
        },
        {
            reactQueryKeys: QUERY_KEYS.WORKDAYS,
            successMessage: t('updated-success'),
            onSuccess: () => {
                refetchData()
                onClose()
            },
        }
    )
    const { control, handleSubmit, reset } = useForm({
        resolver: yupResolver(updateWorkDaySchema(t)),
        defaultValues: {
            work_day_note: workDayNote || '',
            work_n_day: workNDay,
        },
    })

    useEffect(() => {
        reset({
            work_day_note: workDayNote || '',
            work_n_day: workNDay,
        })
    }, [workDayNote, workNDay, reset])
    const onSubmit = async (data: {
        work_day_note: string
        work_n_day: number
    }) => {
        if (selectedWorkDayId) {
            await updateWorkNDay({
                work_day_id: selectedWorkDayId,
                work_day_note: data.work_day_note,
                work_n_day: data.work_n_day,
            })
        }
    }

    return (
        <Dialog open={open} onClose={onClose} title={t('workday-edit')}>
            <Typogaphy size="small">
                {t('workday-account-list')}{' '}
                <span className="text-blue-400">
                    [ID - {selectedWorkDayId}]
                </span>{' '}
                {t('workday-account-description')}
            </Typogaphy>

            <form onSubmit={handleSubmit(onSubmit)}>
                <FormItem
                    control={control}
                    name="work_day_note"
                    type="textarea"
                    placeholder={t('workday-note-label')}
                    className="mt-4"
                />
                <FormItem
                    control={control}
                    name="work_n_day"
                    type="select"
                    placeholder={t('work-n-day-label')}
                    options={[
                        { value: 0, label: '0' },
                        { value: 0.5, label: '0.5' },
                        { value: 1, label: '1' },
                    ]}
                    className="mt-4"
                />
                <div className="flex justify-end mt-4 gap-4">
                    <Button size="small" variant="outlined" onClick={onClose}>
                        {t('cancle-operation')}
                    </Button>
                    <Button variant="contained" type="submit">
                        {t('confirm')}
                    </Button>
                </div>
            </form>
        </Dialog>
    )
}

export default UpdateWorkDay
